import React, { useEffect, useState } from "react";
import { useSetPageTitle, useSharedState, useUrlParams } from "../../hooks";
import { Button, Card, Icon, IconAndText, IconDefinitions } from "../../components";
import { AddClosureModal } from "./components";
import { SelectionRange } from "@progress/kendo-react-dateinputs";
import { usePageActions } from "../PageLayout";
import { LocationClosureModel } from "../../data/entities";
import { showErrorMessage, showSuccessMessage, formatDateLong, getDaysBetween, getMonthDayDisplay, toYearMonthDayLocalString } from "../../utils";
import { LocationClosureService } from "../../services";
import style from "./styles/LocationClosurePage.module.scss";

export function LocationClosurePage() {
  const [closures, setClosures] = useState<LocationClosureModel[]>([]);

  const locationClosureServices = new LocationClosureService();
  const showAddClosureState = useSharedState<boolean>(false);
  const locationId = parseInt(useUrlParams("id"));

  useSetPageTitle("Closure Dates", []);
  usePageActions(
    <Button onClick={() => showAddClosureState.setter(true)}>
      <IconAndText iconName={IconDefinitions.add} text="Add Closure" />
    </Button>
  );

  useEffect(() => {
    if (!locationId) return;

    const initalClosures = async () => {
      const closures = await locationClosureServices.getAllClosuresByLocationIdAsync(locationId);
      setClosures(closures);
    };

    initalClosures();
  }, [locationId]);

  const handleAddClosure = async (dates: SelectionRange) => {
    if (!dates.start || !dates.end) {
      showErrorMessage("Start date and end date must be selected.");
      return;
    }

    const newClosure: LocationClosureModel = {
      id: 0,
      locationId: locationId ?? 0,
      startDate: toYearMonthDayLocalString(dates.start),
      endDate: toYearMonthDayLocalString(dates.end),
    };

    try {
      const locationClosure = await locationClosureServices.insertClosureAsync(newClosure);
      if (locationClosure) {
        const locationClosureNew = {
          id: locationClosure.id,
          locationId: locationClosure.locationId,
          startDate: locationClosure.startDate,
          endDate: locationClosure.endDate,
        } as LocationClosureModel;

        setClosures((prevClosures) => [...prevClosures, locationClosureNew]);
      }
      showSuccessMessage("Closure added successfully.");
      showAddClosureState.setter(false);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 
                           error?.message || 
                          "Failed to add closure. Please try again.";
      showErrorMessage(errorMessage);
      showAddClosureState.setter(false);
    }
  };

  const handleRemove = async (closureId: number) => {
    try {
      await locationClosureServices.delete(closureId);
      setClosures(closures.filter((c) => c.id !== closureId));
      showSuccessMessage("Successfully removed closure");
    } catch {
      showErrorMessage("Failed to remove closure. Please try again.");
    }
  };

  const getDuration = (start: Date | string, end: Date | string) => {
    const days = getDaysBetween(start, end);
    return days === 1 ? "1 day" : `${days} days`;
  };

  return (
    <Card>
      {/* Header Section */}
      <div className={style.header}>
        <div className={style.headerIcon}>
          <span role="img" aria-label="island">🏝️</span>
        </div>
        <div className={style.headerContent}>
          <h3 className={style.headerTitle}>Scheduled Closures</h3>
          <p className={style.headerSubtitle}>
            These closures override all provider schedules and will block appointments during the specified dates.
          </p>
        </div>
      </div>

      {/* Closures List */}
      <div>
        {closures.length === 0 ? (
          <div className={style.emptyState}>
            <div className={style.emptyStateIcon}>
              <Icon iconName={IconDefinitions.calendar} />
            </div>
            <h4 className={style.emptyStateTitle}>No closures scheduled</h4>
            <p className={style.emptyStateText}>
              Add your first closure to block off dates when the location will be unavailable.
            </p>
          </div>
        ) : (
          <div className={style.closuresList}>
            {closures.map((closure) => {
              const startDisplay = getMonthDayDisplay(closure.startDate);
              return (
                <div className={style.closureCard} key={closure.id}>
                  <div className={style.iconCircle}>
                    <div className={style.calendarMonth}>{startDisplay.month}</div>
                    <div className={style.calendarDay}>{startDisplay.day}</div>
                  </div>
                  <div className={style.datesStacked}>
                    <div className={style.dateRow}>
                      {formatDateLong(closure.startDate)} → {formatDateLong(closure.endDate)}
                    </div>
                    <div className={style.durationRow}>
                      {getDuration(closure.startDate, closure.endDate)} closure period
                    </div>
                  </div>
                  <span className={style.statusBadge}>Closed</span>
                  <button
                    onClick={() => handleRemove(closure.id)}
                    className={style.deleteBtn}
                    aria-label="Delete closure"
                  >
                    <Icon iconName={IconDefinitions.delete} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <AddClosureModal onSubmit={handleAddClosure} showState={showAddClosureState} />
    </Card>
  );
}

export default LocationClosurePage;